import { HoverCard, HoverCardContent, HoverCardTrigger } from "@portal/ui";
import { Suspense, useState, type ReactNode } from "react";
import { useMemoDetail } from "../../api/queries";
import { LazyMarkdownView } from "../../components/lazyMarkdown";
import { MemoIcon } from "./MemoIcon";

/**
 * メモの一覧の行にカーソルを乗せると、本文の先頭を Markdown で描いて見せる(ユーザー要望 2026-10-15)。
 * 一覧には抜粋を出さず、行は題名だけにする。本文は、保存済みのメモなら詳細を取って
 * (行に乗せたときに先読みしている)、下書きならその本文を使う。長い本文は数行で切り、下をぼかす。
 * ポインターだけの操作(タッチでは出ない)。中身はメモを開けば読めるので、読み上げには出さない。
 */
export function MemoHoverPreview({
  title,
  icon,
  memoId,
  body,
  children,
}: {
  /** 題名とアイコン。カードの先頭に出す(ユーザー要望 2026-10-15)。 */
  title: string;
  icon: string;
  /** 保存済みのメモ。開いたときに詳細を取る。 */
  memoId?: string;
  /** 下書きなど、手元にある本文。 */
  body?: string;
  /** 行(カーソルを乗せる所)。 */
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const detail = useMemoDetail(open && memoId !== undefined ? memoId : null);
  const source = body ?? detail.data?.memo.body;
  return (
    <HoverCard open={open} onOpenChange={setOpen} openDelay={200} closeDelay={0}>
      <HoverCardTrigger asChild>{children}</HoverCardTrigger>
      <HoverCardContent
        side="left"
        align="start"
        // 行の左の余白にある選択のチェック(幅 24px)に被らないよう、その外に出す
        sideOffset={32}
        data-testid="memo-preview"
        // 見せるだけ。カードに乗せても開いたままにしない(行から離れたら閉じる。ユーザー要望 2026-10-15)
        className="pointer-events-none w-96 p-0"
      >
        <p className="flex items-center gap-2 border-b px-4 py-2 text-sm font-medium">
          <MemoIcon value={icon} />
          <span className="truncate">{title || "無題のメモ"}</span>
        </p>
        <div className="relative max-h-64 overflow-hidden px-4 py-3 text-sm">
          {source === undefined ? (
            <p className="text-muted-foreground">読み込み中…</p>
          ) : source.trim() === "" ? (
            <p className="text-muted-foreground">本文はありません。</p>
          ) : (
            <Suspense fallback={<p className="text-muted-foreground">読み込み中…</p>}>
              {/* エディタの表示は文字・見出しが大きいので、カードの中では小さくする(見出しは太字で見分ける) */}
              <div className="[&_.wmde-markdown]:!bg-transparent [&_.wmde-markdown]:!text-sm [&_.wmde-markdown_:is(h1,h2,h3,h4,h5,h6)]:!mt-2 [&_.wmde-markdown_:is(h1,h2,h3,h4,h5,h6)]:!mb-1 [&_.wmde-markdown_:is(h1,h2,h3,h4,h5,h6)]:!pb-0.5 [&_.wmde-markdown_:is(h1,h2,h3,h4,h5,h6):first-child]:!mt-0 [&_.wmde-markdown_:is(h3,h4,h5,h6)]:!text-sm [&_.wmde-markdown_h1]:!text-base [&_.wmde-markdown_h2]:!text-sm">
                <LazyMarkdownView source={source} />
              </div>
            </Suspense>
          )}
          {/* 続きがあることが分かるように、下をぼかす */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute inset-x-0 bottom-0 h-8 bg-gradient-to-t from-popover"
          />
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
